import { findParentNode } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { isDefined } from '@fabric-app/utils/guards';
import { generateSelectorPath } from '@fabric-app/utils/element';
import { createStyledClone } from './createStyledClone.js';

const notValidDeep = [
  "paragraph",
  "heading"
];
const nonDraggableNodes = [
  "doc",
  "taskList",
  "tableRow",
  "tableCell",
  "tableHeader",
  "orderedList",
  "bulletList",
  "dropdownItemContent",
  "dropdownItemTitle"
];
const blacklistedSelectors = [
  ".ProseMirror-widget"
];
const defaultOffset = 10;
const xOffsets = [
  0,
  10,
  20,
  30,
  40,
  50,
  60,
  70,
  80,
  90,
  100
];
const elementOffset = {
  listItem: (node, editor, pos) => {
    const parent = findParentNode((node2) => node2.type.name === "bulletList" || node2.type.name === "orderedList")(NodeSelection.create(editor.state.doc, pos));
    if (!parent) {
      return defaultOffset;
    }
    const listType = parent.node.type.name;
    return listType === "bulletList" ? 25 : defaultOffset;
  },
  taskItem: () => 32,
  codeBlock: () => ({
    mainAxis: 26,
    crossAxis: -10
  }),
  dropdownItem: () => 34,
  dropdownItemTitle: () => 34
};
const parentSelectors = {
  listItem: {
    selector: "ul,ol",
    deep: false,
    rootNode: "ol"
  },
  taskItem: {
    selector: 'div[data-node-view-wrapper],div[class="node-taskItem"]',
    deep: true
  },
  table: {
    selector: "table",
    deep: true
  },
  codeBlock: {
    selector: "pre",
    deep: true
  }
};
const isValidParent = (node, depth, pos) => {
  if (nonDraggableNodes.includes(node.type.name)) {
    return false;
  }
  let allowDeepNode = false;
  if (pos) {
    for (let i = pos.depth; i >= 0; i--) {
      const parent = pos.node(i);
      if (parent.type.name === "listItem") break;
      if (parent.type.name === "dropdownItem") break;
      if (parent.type.name === "dropdownItemContent") {
        allowDeepNode = true;
        break;
      }
    }
  }
  if (notValidDeep.includes(node.type.name) && depth > 1 && !allowDeepNode) {
    return false;
  }
  return node.isBlock;
};
const getElementOffset = (node, editor, pos) => {
  return elementOffset[node.type.name]?.(node, editor, pos) ?? defaultOffset;
};
const getParentNodes = (node, viewDom, nodeType) => {
  const parentNodes = [];
  const stopSelector = parentSelectors[nodeType];
  if (!stopSelector) {
    return {
      nodes: parentNodes,
      selector: null
    };
  }
  let currentNode = node.parentElement;
  while (isDefined(currentNode) && currentNode !== viewDom && currentNode !== document.body && isDefined(currentNode.parentElement)) {
    parentNodes.push(currentNode);
    if (currentNode.matches(stopSelector.selector)) {
      break;
    }
    currentNode = currentNode.parentElement;
  }
  return {
    nodes: parentNodes,
    selector: stopSelector
  };
};
const findRootNode = (element, rootNode) => {
  let root = element.closest(rootNode);
  while (root && root.parentElement) {
    const parentMatch = root.parentElement.closest(rootNode);
    if (!parentMatch) break;
    root = parentMatch;
  }
  return root;
};
const createDragPreview = (element, viewDom, nodeType) => {
  const { nodes: parentNodes, selector } = getParentNodes(element, viewDom, nodeType);
  if (!selector) {
    return createSimplePreview(element);
  }
  const rootNode = selector.rootNode ? findRootNode(element, selector.rootNode) : null;
  if (rootNode) {
    return createRootBasedPreview(element, rootNode);
  }
  if (selector.deep) {
    return createDeepClonePreview(parentNodes, selector.selector);
  }
  return createHierarchyPreview(parentNodes, element);
};
const cleanupDragPreviewElement = (element) => {
  blacklistedSelectors.forEach((selector) => {
    element.querySelectorAll(selector).forEach((el) => el.remove());
  });
  element.querySelectorAll(".ProseMirror-yjs-selection").forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.backgroundColor = "transparent";
    }
  });
  return element;
};
const createSimplePreview = (element) => {
  const clone = createStyledClone(element);
  if (!(clone instanceof Element)) {
    return null;
  }
  const container = document.createElement("div");
  container.appendChild(clone);
  return cleanupDragPreviewElement(container);
};
const createRootBasedPreview = (element, rootNode) => {
  const elementSelector = generateSelectorPath(rootNode, element);
  const clone = createStyledClone(rootNode, true);
  if (!(clone instanceof Element)) {
    return null;
  }
  const clonedElement = clone.querySelector(elementSelector);
  if (!clonedElement) {
    return null;
  }
  clone.querySelectorAll("*").forEach((el) => {
    if (el instanceof HTMLElement) {
      if (el !== clonedElement && !el.contains(clonedElement) && !clonedElement.contains(el)) {
        el.style.position = "absolute";
        el.style.opacity = "0";
      } else if (el !== clonedElement && el.contains(clonedElement)) {
        el.style.margin = "0";
        el.style.padding = "0";
        el.style.gap = "0";
        el.style.borderSpacing = "0";
        el.style.columnGap = "0";
        el.style.rowGap = "0";
        const style = document.createElement("style");
        const id = el.id || el.getAttribute("data-uuid");
        style.textContent = `
          ${el.tagName}#${id}::before,
          ${el.tagName}#${id}::after {
            opacity: 0;
            position: absolute;
          }
        `;
        el.appendChild(style);
      }
    }
  });
  const container = document.createElement("div");
  container.appendChild(clone);
  return cleanupDragPreviewElement(container);
};
const createDeepClonePreview = (parentNodes, selector) => {
  const matchingParentIndex = parentNodes.findIndex((node) => node.matches(selector));
  if (matchingParentIndex >= 0) {
    const matchingParent = parentNodes[matchingParentIndex];
    const clone = createStyledClone(matchingParent, true);
    if (!(clone instanceof Element)) {
      return null;
    }
    const container = document.createElement("div");
    container.appendChild(clone);
    return cleanupDragPreviewElement(container);
  }
  return null;
};
const createHierarchyPreview = (parentNodes, element) => {
  let lastParent = null;
  const container = document.createElement("div");
  for (const node of parentNodes) {
    const clone = createStyledClone(node, false);
    if (!(clone instanceof Element)) {
      return null;
    }
    if (lastParent) {
      lastParent.appendChild(clone);
    } else {
      container.appendChild(clone);
    }
    lastParent = clone;
  }
  const clonedElement = createStyledClone(element);
  if (!(clonedElement instanceof Element)) {
    return null;
  }
  if (lastParent) {
    lastParent.appendChild(clonedElement);
  } else {
    container.appendChild(clonedElement);
  }
  return cleanupDragPreviewElement(container);
};
const cleanupDragPreviews = () => {
  document.querySelectorAll('[data-drag-handle-preview="true"]').forEach((el) => el.remove());
};
const getNodeAtPos = (editor, x, y) => {
  let blockParent = null;
  for (let offsetIndex = 0; offsetIndex < xOffsets.length && !blockParent; offsetIndex++) {
    const offset = xOffsets[offsetIndex];
    const coords = editor.view.posAtCoords({
      left: x + offset,
      top: y
    });
    if (!coords) {
      continue;
    }
    const resolved = editor.state.doc.resolve(coords.pos);
    blockParent = findValidParentFromPos(resolved);
  }
  return blockParent;
};
const findValidParentFromPos = (resolved) => {
  let element = isValidParent(resolved.node(), resolved.depth, resolved) ? {
    pos: resolved.before(1),
    start: resolved.start(),
    depth: resolved.depth,
    node: resolved.node()
  } : void 0;
  if (!element) {
    element = searchParentHierarchy(resolved);
  }
  return element && element.depth > 0 ? element : null;
};
const searchParentHierarchy = (resolved) => {
  let searchDepth = resolved.depth;
  while (searchDepth >= 0) {
    const node = resolved.node(searchDepth);
    if (node.type.name === "doc") {
      break;
    }
    if (isValidParent(node, searchDepth, resolved)) {
      return {
        pos: searchDepth > 0 ? resolved.before(searchDepth) : 0,
        start: resolved.start(searchDepth),
        depth: searchDepth,
        node
      };
    }
    searchDepth--;
  }
  return void 0;
};

export { blacklistedSelectors, cleanupDragPreviewElement, cleanupDragPreviews, createDragPreview, defaultOffset, elementOffset, findRootNode, getElementOffset, getNodeAtPos, getParentNodes, isValidParent, nonDraggableNodes, notValidDeep, parentSelectors, xOffsets };
//# sourceMappingURL=dragHandleUtils.js.map
